/**
 * chat 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('chat:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const chatZh: TranslationDict = {

  // ======================== 聊天区域 / ChatArea ========================
  'onlineCount': '在线人数',
  'selectConversation': '选择一个对话开始',
  'createFirstGroup': '创建第一个群聊',
  'selectFromListHint': '或从左侧列表选择已有对话',
  'createNewGroup': '创建新群聊',
  'cancel': '取消',
  'create': '创建',
  'inviteMembers': '邀请成员',
  'search': '搜索',
  'unmute': '取消免打扰',
  'mute': '开启免打扰',
  'groupSettings': '群聊设置',
  'sessionList': '会话列表',
  'typeMessage': '输入消息...',
  'send': '发送',
  'loading': '加载中...',
  'createFailed': '创建失败',
  'groupName': '群聊名称 *',
  'groupNamePlaceholder': '给群聊起个名字',
  'creating': '创建中...',
  'inviteFailedRetry': '邀请失败，请重试',
  'enterValidId': '请输入有效的 ID',
  'inviteFailed': '邀请失败',
  'alreadyInGroup': '对方已在群聊中！',
  'inviteSearchHint': '搜索用户或 AI 并选择邀请',
  'searchNamePlaceholder': '输入名称搜索...',
  'inviteSearchPrompt': '输入关键词搜索要邀请的成员',
  'selectedMembers': '已选 {size} 位成员',
  'manualId': '+ 手动输入 ID',
  'manualIdPlaceholder': '输入 ID',
  'invite': '邀请',
  'inviteSuccess': '已成功邀请 {success} 位成员',
  'invitationSent': '已发送邀请',
  'joined': '已加入',
  'addMembers': '添加成员',
  'searchMembers': '搜索成员...',
  'human': '人类',
  'inviting': '邀请中...',
  'inviteCount': '邀请 ({count})',
  'reconnecting': '连接断开，正在重新连接…',
  'jumpToFirstUnread': '跳至首个未读',
  'beginningOfChat': '—— 已到聊天开头 ——',
  'startDMConversation': '开始你们的对话吧',
  'startGroupConversation': '暂无消息，发送第一条消息吧',
  'newMessages': '新消息',
  'scrollToBottom': '回到底部',
  'addAttachment': '添加附件',
  'addEmoji': '添加表情',
  'emojiPacks': '表情包',

  // @ 是全字匹配：手打 @短名（漏了括号后缀）时提醒一句，并可一键补全
  'mentionHint': '「@{token}」没 @ 到人，是想 @{name} 吗？',
  'mentionHintFix': '补全',
  'actions': '操作',
  'reply': '回复',
  'copy': '复制',
  'copied': '已复制',
  'revoke': '撤回',
  'revokedMessage': '{name} 撤回了一条消息',
  'revokedChannelFailed': '站内已撤回，但外部通道没撤掉：{reason}',
  'dmInputPlaceholder': '输入私信... Enter 发送',
  'groupInputPlaceholder': '输入消息... @AI名称 提及, Enter 发送',
  'uploadingFiles': '还有 {count} 个文件正在上传...',
  'uploadErrorRemove': '有文件上传失败，请先移除',
  'sendAttachment': '发送附件',
  'uploadFailed': '上传失败',
  'dropToAdd': '拖动到此处上传文件',
  'worldGateTitle': '这个群聊绑定了群视界',
  'worldGateSubtitle': '世界已就绪，选择一种方式进入',
  'worldGateImmersive': '在沉浸界面打开',
  'worldGateStandard': '在此标准界面打开',
  'worldEnterImmersive': '去沉浸式界面',
  'thinking': '思考中...',
  'typing': '输入中...',
  'viewProfile': '查看 {name} 资料',
  'fromInstance': '来自实例: {publicId}',
  'fromQq': '来自 QQ',
  'reconnectingShort': '重连中...',
  'federated': '联邦',
  'federatedGroup': '此对话已开启联邦共享，消息会同步到其他实例',
  'aiNotDiscoverable': '此 AI 未开放查找',
}

export const chatEn: TranslationDict = {
  'onlineCount': 'Online',
  'selectConversation': 'Select a conversation to start',
  'createFirstGroup': 'Create first group',
  'selectFromListHint': 'Or select from the list on the left',
  'createNewGroup': 'Create New Group',
  'cancel': 'Cancel',
  'create': 'Create',
  'inviteMembers': 'Invite Members',
  'search': 'Search',
  'unmute': 'Unmute',
  'mute': 'Mute',
  'groupSettings': 'Group Settings',
  'sessionList': 'Session List',
  'typeMessage': 'Type a message...',
  'send': 'Send',
  'loading': 'Loading...',
  'createFailed': 'Creation failed',
  'groupName': 'Group Name *',
  'groupNamePlaceholder': 'Name your group',
  'creating': 'Creating...',
  'inviteFailedRetry': 'Invitation failed, please retry',
  'enterValidId': 'Please enter a valid ID',
  'inviteFailed': 'Invitation failed',
  'alreadyInGroup': 'Already in the group!',
  'inviteSearchHint': 'Search for users or AI and select to invite',
  'searchNamePlaceholder': 'Search by name...',
  'inviteSearchPrompt': 'Enter keywords to search for members to invite',
  'selectedMembers': '{size} member(s) selected',
  'manualId': '+ Enter ID manually',
  'manualIdPlaceholder': 'Enter ID',
  'invite': 'Invite',
  'inviteSuccess': 'Successfully invited {success} member(s)',
  'invitationSent': 'Invitation sent',
  'joined': 'Joined',
  'addMembers': 'Add Members',
  'searchMembers': 'Search members...',
  'human': 'Human',
  'inviting': 'Inviting...',
  'inviteCount': 'Invite ({count})',
  'reconnecting': 'Connection lost, reconnecting...',
  'jumpToFirstUnread': 'Jump to first unread',
  'beginningOfChat': '--- Beginning of chat ---',
  'startDMConversation': 'Start your conversation',
  'startGroupConversation': 'No messages yet, send the first one',
  'newMessages': 'New Messages',
  'scrollToBottom': 'Scroll to bottom',
  'addAttachment': 'Add attachment',
  'addEmoji': 'Add emoji',
  'emojiPacks': 'Emoji packs',
  'mentionHint': '"{token}" matched no one — did you mean @{name}?',
  'mentionHintFix': 'Fix',
  'actions': 'Actions',
  'reply': 'Reply',
  'copy': 'Copy',
  'copied': 'Copied',
  'revoke': 'Recall',
  'revokedMessage': '{name} recalled a message',
  'revokedChannelFailed': 'Recalled here, but the external channel could not: {reason}',
  'dmInputPlaceholder': 'Type a DM... Enter to send',
  'groupInputPlaceholder': 'Type a message... @AI to mention, Enter to send',
  'uploadingFiles': '{count} file(s) uploading...',
  'uploadErrorRemove': 'Some files failed to upload, please remove them',
  'sendAttachment': 'Send attachment(s)',
  'uploadFailed': 'Upload failed',
  'dropToAdd': 'Drop files here',
  'worldGateTitle': 'This group is bound to a World',
  'worldGateSubtitle': 'The world is ready — choose how to enter',
  'worldGateImmersive': 'Open in the immersive UI',
  'worldGateStandard': 'Open in this standard UI',
  'worldEnterImmersive': 'Go to the immersive UI',
  'thinking': 'Thinking...',
  'typing': 'Typing...',
  'viewProfile': "View {name}'s profile",
  'fromInstance': 'From instance: {publicId}',
  'fromQq': 'via QQ',
  'reconnectingShort': 'Reconnecting...',
  'federated': 'Federated',
  'federatedGroup': 'This conversation has federation sharing enabled; messages will be synced to other instances.',
  'aiNotDiscoverable': 'This AI is not discoverable',
}

export const chatJa: TranslationDict = {
  'onlineCount': 'オンライン人数',
  'selectConversation': '会話を選択して開始',
  'createFirstGroup': '最初のグループを作成',
  'selectFromListHint': 'または左のリストから選択してください',
  'createNewGroup': '新しいグループを作成',
  'cancel': 'キャンセル',
  'create': '作成',
  'inviteMembers': 'メンバーを招待',
  'search': '検索',
  'unmute': 'おやすみ解除',
  'mute': 'おやすみ',
  'groupSettings': 'グループ設定',
  'sessionList': 'セッション一覧',
  'typeMessage': 'メッセージを入力...',
  'send': '送信',
  'loading': '読み込み中...',
  'createFailed': '作成に失敗しました',
  'groupName': 'グループ名 *',
  'groupNamePlaceholder': 'グループの名前を入力',
  'creating': '作成中...',
  'inviteFailedRetry': '招待に失敗しました。再試行してください',
  'enterValidId': '有効なIDを入力してください',
  'inviteFailed': '招待に失敗しました',
  'alreadyInGroup': 'すでにグループに参加しています！',
  'inviteSearchHint': 'ユーザーまたはAIを検索し、選択して招待',
  'searchNamePlaceholder': '名前で検索...',
  'inviteSearchPrompt': 'キーワードを入力して招待するメンバーを検索',
  'selectedMembers': '{size} 人選択中',
  'manualId': '+ IDを手動入力',
  'manualIdPlaceholder': 'IDを入力',
  'invite': '招待',
  'inviteSuccess': '{success} 人の招待に成功しました',
  'invitationSent': '招待を送信しました',
  'joined': '参加しました',
  'addMembers': 'メンバーを追加',
  'searchMembers': 'メンバーを検索...',
  'human': '人間',
  'inviting': '招待中...',
  'inviteCount': '招待 ({count})',
  'reconnecting': '接続が切断されました。再接続中…',
  'jumpToFirstUnread': '最初の未読へ',
  'beginningOfChat': '—— チャットの先頭 ——',
  'startDMConversation': '会話を始めましょう',
  'startGroupConversation': 'まだメッセージはありません。最初のメッセージを送信しましょう',
  'newMessages': '新しいメッセージ',
  'scrollToBottom': '一番下へスクロール',
  'addAttachment': '添付ファイルを追加',
  'addEmoji': '絵文字を追加',
  'emojiPacks': '絵文字パック',
  'mentionHint': '「@{token}」は誰にも一致しません。@{name} ですか？',
  'mentionHintFix': '補完',
  'actions': '操作',
  'reply': '返信',
  'copy': 'コピー',
  'copied': 'コピーしました',
  'revoke': '撤回',
  'revokedMessage': '{name} がメッセージを撤回しました',
  'revokedChannelFailed': 'サイト内では撤回しましたが、外部チャネルでは撤回できませんでした：{reason}',
  'dmInputPlaceholder': 'DMを入力... Enterで送信',
  'groupInputPlaceholder': 'メッセージを入力... @AI名 でメンション、Enterで送信',
  'uploadingFiles': '{count} 個のファイルをアップロード中...',
  'uploadErrorRemove': 'アップロードに失敗したファイルがあります。先に削除してください',
  'sendAttachment': '添付ファイルを送信',
  'uploadFailed': 'アップロード失敗',
  'dropToAdd': 'ここにファイルをドロップ',
  'worldGateTitle': 'このグループはワールドにバインドされています',
  'worldGateSubtitle': 'ワールドの準備ができました。入り方を選択してください',
  'worldGateImmersive': 'イマーシブUIで開く',
  'worldGateStandard': 'この標準UIで開く',
  'worldEnterImmersive': 'イマーシブUIへ',
  'thinking': '思考中...',
  'typing': '入力中...',
  'viewProfile': '{name} のプロフィールを表示',
  'fromInstance': 'インスタンス: {publicId}',
  'fromQq': 'QQから',
  'reconnectingShort': '再接続中...',
  'federated': '連合',
  'federatedGroup': 'この会話は連合共有が有効です。メッセージは他のインスタンスに同期されます。',
  'aiNotDiscoverable': 'このAIは検出不可に設定されています',
}
